import { HttpClient, HttpParams, HttpResponse } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { Observable, map, shareReplay } from 'rxjs';
import { environment } from '../../environment';
import { normalizeApiArray } from '../utils/api-response.util';
import {
  FileDownload,
  ReportAccessGrant,
  ReportConfiguration,
  ReportDefinition,
  ReportExecution,
  ReportFilterOptionResponse,
  ReportPreview,
  ReportQuery,
  ReportSchedule
} from '../models/reporting.model';

@Injectable({ providedIn: 'root' })
export class ReportingService {
  private readonly apiUrl = `${environment.apiUrl}/reports`;
  private catalogueRequest?: Observable<ReportDefinition[]>;

  constructor(private http: HttpClient) {}

  getCatalogue(refresh = false): Observable<ReportDefinition[]> {
    if (!this.catalogueRequest || refresh) {
      this.catalogueRequest = this.http.get<unknown>(`${this.apiUrl}/catalogue`).pipe(
        map(response => normalizeApiArray<ReportDefinition>(response).filter(report => report.canView)),
        shareReplay({ bufferSize: 1, refCount: false })
      );
    }
    return this.catalogueRequest;
  }

  preview(reportKey: string, query: ReportQuery): Observable<ReportPreview> {
    return this.http.post<ReportPreview>(`${this.apiUrl}/${encodeURIComponent(reportKey)}/preview`, query);
  }

  getFilterOptions(
    reportKey: string,
    filterKey: string,
    dependencies: Record<string, unknown>,
    search = '',
    take = 50,
    continuationToken?: string | null
  ): Observable<ReportFilterOptionResponse> {
    let params = new HttpParams()
      .set('filterKey', filterKey)
      .set('take', Math.min(100, Math.max(1, take)));

    for (const key of ['organisationID', 'vehicleID', 'tripID', 'passengerProfileID']) {
      const value = dependencies[key];
      if (value !== null && value !== undefined && value !== '') {
        params = params.set(key, String(value));
      }
    }
    if (search.trim()) params = params.set('search', search.trim());
    if (continuationToken) params = params.set('continuationToken', continuationToken);

    return this.http.get<ReportFilterOptionResponse>(
      `${this.apiUrl}/${encodeURIComponent(reportKey)}/filter-options`,
      { params }
    );
  }

  export(reportKey: string, query: ReportQuery, timeZoneID: string, includeOrganisationBranding: boolean, accentColour?: string): Observable<FileDownload> {
    let params = new HttpParams()
      .set('timeZoneID', timeZoneID || 'Africa/Johannesburg')
      .set('includeOrganisationBranding', includeOrganisationBranding);
    if (accentColour) params = params.set('accentColour', accentColour);

    return this.http.post(`${this.apiUrl}/${encodeURIComponent(reportKey)}/export`, query, {
      params,
      observe: 'response',
      responseType: 'blob'
    }).pipe(map(response => this.toDownload(response, `${reportKey}.xlsx`)));
  }

  getConfigurations(): Observable<ReportConfiguration[]> {
    return this.http.get<unknown>(`${this.apiUrl}/configurations`).pipe(
      map(response => normalizeApiArray<ReportConfiguration>(response))
    );
  }

  getConfiguration(id: number): Observable<ReportConfiguration> {
    return this.http.get<ReportConfiguration>(`${this.apiUrl}/configurations/${id}`);
  }

  createConfiguration(configuration: Omit<ReportConfiguration, 'reportConfigurationID'>): Observable<ReportConfiguration> {
    return this.http.post<ReportConfiguration>(`${this.apiUrl}/configurations`, configuration);
  }

  updateConfiguration(configuration: ReportConfiguration): Observable<ReportConfiguration> {
    return this.http.put<ReportConfiguration>(
      `${this.apiUrl}/configurations/${configuration.reportConfigurationID}`,
      this.withoutID(configuration, 'reportConfigurationID')
    );
  }

  deleteConfiguration(id: number): Observable<void> {
    return this.http.delete<void>(`${this.apiUrl}/configurations/${id}`);
  }

  getSchedules(): Observable<ReportSchedule[]> {
    return this.http.get<unknown>(`${this.apiUrl}/schedules`).pipe(
      map(response => normalizeApiArray<ReportSchedule>(response))
    );
  }

  createSchedule(schedule: Omit<ReportSchedule, 'reportScheduleID'>): Observable<ReportSchedule> {
    return this.http.post<ReportSchedule>(`${this.apiUrl}/schedules`, schedule);
  }

  updateSchedule(schedule: ReportSchedule): Observable<ReportSchedule> {
    return this.http.put<ReportSchedule>(
      `${this.apiUrl}/schedules/${schedule.reportScheduleID}`,
      this.withoutID(schedule, 'reportScheduleID')
    );
  }

  deleteSchedule(id: number): Observable<void> {
    return this.http.delete<void>(`${this.apiUrl}/schedules/${id}`);
  }

  runSchedule(id: number): Observable<unknown> {
    return this.http.post(`${this.apiUrl}/schedules/${id}/run`, {});
  }

  getExecutions(take = 50): Observable<ReportExecution[]> {
    const params = new HttpParams().set('take', Math.min(200, Math.max(1, take)));
    return this.http.get<unknown>(`${this.apiUrl}/executions`, { params }).pipe(
      map(response => normalizeApiArray<ReportExecution>(response))
    );
  }

  downloadExecution(execution: ReportExecution): Observable<FileDownload> {
    return this.http.get(`${this.apiUrl}/executions/${execution.reportExecutionID}/download`, {
      observe: 'response',
      responseType: 'blob'
    }).pipe(map(response => this.toDownload(response, execution.fileName || `report-${execution.reportExecutionID}.xlsx`)));
  }

  getAccessGrants(): Observable<ReportAccessGrant[]> {
    return this.http.get<unknown>(`${this.apiUrl}/access-grants`).pipe(
      map(response => normalizeApiArray<ReportAccessGrant>(response))
    );
  }

  createAccessGrant(grant: Omit<ReportAccessGrant, 'reportAccessGrantID'>): Observable<ReportAccessGrant> {
    return this.http.post<ReportAccessGrant>(`${this.apiUrl}/access-grants`, grant);
  }

  deleteAccessGrant(id: number): Observable<void> {
    return this.http.delete<void>(`${this.apiUrl}/access-grants/${id}`);
  }

  private toDownload(response: HttpResponse<Blob>, fallbackName: string): FileDownload {
    const disposition = response.headers.get('content-disposition') || '';
    const utfName = disposition.match(/filename\*=UTF-8''([^;]+)/i)?.[1];
    const plainName = disposition.match(/filename="?([^";]+)"?/i)?.[1];
    return {
      blob: response.body || new Blob(),
      fileName: decodeURIComponent(utfName || plainName || fallbackName)
    };
  }

  private withoutID<T extends object>(value: T, key: keyof T): Omit<T, keyof T> {
    const copy = { ...value };
    delete copy[key];
    return copy as Omit<T, keyof T>;
  }
}
